import {
  ResponseResult,
  type ResponseType,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import { isDataCollectionType } from '../../../shared/domain/responseTypeClassification';
import { ShowOn } from '../../../../types/helios/Question';

/**
 * The slice of a sub-question definition the parent-derivation
 * helpers need: identity (to match response entries) and the ERT
 * `responseType` (to split SCORED Pass/Fail children from
 * DATA-COLLECTION children). Both fields are optional to structurally
 * match the SDK's `SubQuestion` and the render layer's
 * `DisplayableSubQuestion` — callers pass their definition arrays
 * straight through. A def without an id can never be matched to an
 * entry and is treated as unanswered (conservative: the parent
 * cannot complete).
 *
 * Note: conditional-visibility filtering (`showWhenParentIs`) is
 * handled BEFORE calling these helpers — the render layer filters
 * `DisplayableSubQuestion[]` to only visible subs and passes that
 * pre-filtered array as `subQuestionRefs`. The closure validator
 * applies the visibility guard inline. This keeps `SubQuestionTypeRef`
 * free of view-state fields and the derivation functions stateless.
 */
export interface SubQuestionTypeRef {
  readonly subQuestionId?: string | undefined;
  readonly responseType?: ResponseType | undefined;
}

/**
 * Returns true when a sub-question should be shown / counted given the
 * parent's current committed result:
 *
 *   - Non-conditional sub (`showWhenParentIs` absent) → always visible.
 *   - Parent committed PASS → visible when PASS or BOTH.
 *   - Parent committed FAIL → visible when FAIL or BOTH.
 *   - Parent uncommitted or NA → hidden (the auditor must score the
 *     parent before sub-questions become reachable).
 *
 * Accepts any object that may carry `showWhenParentIs` so it works
 * with both `SubQuestionTypeRef` (V2 logic layer) and the local
 * `SubQuestion` type (closure validators, helpers). If the field is
 * absent on the object, the sub is treated as non-conditional and is
 * always visible.
 *
 * Pure — no react, no side effects. Used by the render layer to filter
 * the visible sub-question list, and by the derivation helpers below to
 * exclude untriggered subs from completion and parent-result derivation.
 */
export function isSubVisible(
  def: { readonly showWhenParentIs?: string | ShowOn | undefined },
  parentResult: string | undefined,
): boolean {
  if (!def.showWhenParentIs) return true; // non-conditional sub always shown
  // Treat showWhenParentIs as the ShowOn enum regardless of whether the
  // caller passes a typed ShowOn or a plain string — the values are identical
  // ('PASS', 'FAIL', 'BOTH') so the comparison is always correct.
  const showOn = def.showWhenParentIs as ShowOn;
  if (parentResult === ResponseResult.PASS) {
    return showOn === ShowOn.PASS || showOn === ShowOn.BOTH;
  }
  if (parentResult === ResponseResult.FAIL) {
    return showOn === ShowOn.FAIL || showOn === ShowOn.BOTH;
  }
  // NA or unanswered — all conditional subs are hidden until parent commits PASS/FAIL
  return false;
}

/**
 * Whether a READ-shape sub-response entry counts as answered for the
 * given sub-question type: a data-collection sub is answered by a
 * non-empty `responseValue`; a scored (Pass/Fail) sub is answered by
 * a committed `responseResult`. The read-side twin of
 * `isSubResponseAnswered` in deriveSubResponsesStatus (which serves
 * the WIRE shape).
 *
 * Pure — no react, no side effects. Exported for the nav / display
 * layers so "answered" renders identically everywhere.
 */
export function isSubEntryAnswered(
  entry: SubQuestionResponseEntry | undefined,
  responseType: ResponseType | undefined,
): boolean {
  if (!entry) return false;
  if (isDataCollectionType(responseType)) {
    return entry.responseValue !== undefined && entry.responseValue !== '';
  }
  return entry.response?.responseResult !== undefined;
}

/** Index response entries by sub-question id for def-driven walks. */
function indexEntriesById(
  subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined,
): ReadonlyMap<string, SubQuestionResponseEntry> {
  const byId = new Map<string, SubQuestionResponseEntry>();
  for (const entry of subQuestionResponses ?? []) {
    if (entry.subQuestionId) byId.set(entry.subQuestionId, entry);
  }
  return byId;
}

/**
 * Defs that participate in Pass/Fail aggregation (non-ERT children).
 * Exported for the bulk-answer path, which stamps results onto
 * exactly this subset.
 */
export function scoredDefs(defs: readonly SubQuestionTypeRef[]): SubQuestionTypeRef[] {
  return defs.filter((def) => !isDataCollectionType(def.responseType));
}

/**
 * Ids of the scored (Pass/Fail) children — the bulk write's targets.
 * Defs without an id are dropped (they can't be addressed on the
 * wire).
 */
export function scoredSubQuestionIds(defs: readonly SubQuestionTypeRef[]): string[] {
  return scoredDefs(defs)
    .map((def) => def.subQuestionId)
    .filter((id): id is string => Boolean(id));
}

/**
 * Derives the parent question's aggregated `responseResult` from
 * the current set of sub-question answers. Only SCORED (Pass/Fail)
 * children participate — data-collection children capture values,
 * not results, and never move the aggregate.
 *
 * For questions with conditional sub-questions, callers should
 * pre-filter the `subQuestionDefs` array to only the currently
 * VISIBLE subs (using `isSubVisible`) before calling this function —
 * the render layer does this via `visibleSubQuestions`, and
 * `isQuestionCompleted` filters inline.
 *
 * FAIL is dominant and can be reported as soon as any answered
 * scored child fails, even while other children are missing or
 * unanswered. Without a FAIL, the parent stays unanswered until
 * every scored child is answered. A complete parent is PASS when
 * any scored child passes; otherwise every scored child is NA and
 * the parent is NA.
 *
 * A question with ZERO scored children (data-only) returns
 * undefined: aggregation does not apply — the auditor scores the
 * parent directly (self-scoring parent), and this helper must not
 * overwrite that answer.
 *
 *   3 P/F subs, PASS + (unanswered) + FAIL → FAIL
 *   3 P/F subs, PASS + NA + (unanswered) → undefined
 *   2 P/F + 1 data sub, PASS + NA + (anything) → PASS
 *   2 data subs only → undefined (self-scoring parent)
 *   0 subs → undefined (defensive; caller shouldn't invoke here)
 *
 * Pure — no react, no side effects. Consumed by optimistic audit
 * cache patches so parent result badges and metrics update on tap.
 */
export function deriveParentResult(
  subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined,
  subQuestionDefs: readonly SubQuestionTypeRef[],
): ResponseResult | undefined {
  const scored = scoredDefs(subQuestionDefs);
  if (scored.length === 0) return undefined;

  const entriesById = indexEntriesById(subQuestionResponses);

  let hasPass = false;
  let allAnswered = true;
  for (const def of scored) {
    const entry = def.subQuestionId ? entriesById.get(def.subQuestionId) : undefined;
    const result = entry?.response?.responseResult;
    if (result === ResponseResult.FAIL) return ResponseResult.FAIL;
    if (result === undefined) allAnswered = false;
    if (result === ResponseResult.PASS) hasPass = true;
  }
  if (!allAnswered) return undefined;
  return hasPass ? ResponseResult.PASS : ResponseResult.NA;
}

/**
 * Derives whether the parent question is COMPLETED: every child
 * sub-question is answered — a scored child by its committed
 * `responseResult`, a data-collection child by a non-empty
 * `responseValue` (see {@link isSubEntryAnswered}).
 *
 * For questions with conditional sub-questions, callers should
 * pre-filter `subQuestionDefs` to only visible subs before calling
 * this function — see `isQuestionCompleted` for an example.
 *
 *   3 P/F subs, all PASS → true
 *   2 P/F + 1 data sub, PASS + FAIL + valued → true
 *   2 P/F + 1 data sub, PASS + FAIL + (no value) → false
 *   3 subs, no responses cached → false
 *   0 subs → false (caller shouldn't invoke here)
 *
 * Pure — no react, no side effects. Drives the
 * `audit.metrics.completedQuestions` counter and sticky-nav progress.
 */
export function deriveParentCompletion(
  subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined,
  subQuestionDefs: readonly SubQuestionTypeRef[],
): boolean {
  if (subQuestionDefs.length === 0) return false;
  const entriesById = indexEntriesById(subQuestionResponses);
  return subQuestionDefs.every((def) =>
    isSubEntryAnswered(
      def.subQuestionId ? entriesById.get(def.subQuestionId) : undefined,
      def.responseType,
    ),
  );
}
